import { gql, useQuery } from '@apollo/client';
import {
  CardDescription,
  CardHeader,
  CardTitle,
  Heading,
  Text,
} from '@apollo/orbit';
import React from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { UpgradeButton } from 'src/components/upgradeButton/UpgradeButton';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentSubscription } from 'src/hooks/useCurrentSubscription';
import { useShowSelfServeScaleUI } from 'src/lib/featureFlags';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { planNameMapper } from 'src/lib/planMappers';

import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from './ChampionDashboardCard';

const PLAN_INFO_QUERY = gql<
  GraphQLTypes.ChampionDashboardPlanWidget,
  GraphQLTypes.ChampionDashboardPlanWidgetVariables
>`
  query ChampionDashboardPlanWidget($accountId: ID!) {
    account(id: $accountId) {
      id
      memberships {
        user {
          id
        }
        account {
          id
        }
      }
    }
  }
`;

export const PlanInfoWidget = ({ accountId }: { accountId: string }) => {
  const showSelfServeScaleUI = useShowSelfServeScaleUI();
  const { data, loading } = useQuery(PLAN_INFO_QUERY, {
    pollInterval: 60_000,
    variables: {
      accountId,
    },
  });

  const { currentPlan, loading: currentPlanLoading } =
    useCurrentPlan({ accountId }) ?? {};

  const tier = currentPlan?.tier;
  const name = planNameMapper(currentPlan?.name, tier, currentPlan?.kind);
  const { isUsageBasedPlan, isEnterprisePlan } = useCheckPlanTypes({
    tier,
    kind: currentPlan?.kind,
  });
  const { currentSubscription } = useCurrentSubscription({
    accountId,
    skip: !isUsageBasedPlan,
  });

  return (
    <ChampionDashboardCard className="col-span-full lg:col-span-3 xl:col-span-2">
      <CardHeader>
        <CardTitle>Plan info</CardTitle>
        {name && (
          <CardDescription>
            You're on the{' '}
            <Text as="span" className="capitalize" variant="bodySb" size="base">
              {name?.toLowerCase()}
            </Text>{' '}
            plan
          </CardDescription>
        )}
        {data?.account && !isEnterprisePlan ? (
          <UpgradeButton accountId={data.account.id} variant="secondary">
            {showSelfServeScaleUI ? 'Upgrade' : 'Request upgrade'}
          </UpgradeButton>
        ) : null}
      </CardHeader>
      <ChampionDashboardCardBody>
        {loading || currentPlanLoading || !currentPlan ? (
          <Loading />
        ) : (
          <div className="flex h-full items-center">
            <div className="flex flex-1 items-start gap-1">
              <Stat
                label="Data retention duration"
                value={
                  currentPlan.maxRangeInDays
                    ? `${currentPlan.maxRangeInDays} ${format.pluralize(
                        currentPlan.maxRangeInDays,
                        'day',
                      )}`
                    : 'n/a'
                }
              />
              {isUsageBasedPlan ? (
                <>
                  {currentSubscription?.maxServerlessRequestsPerMonth && (
                    <Stat
                      label="Monthly serverless operations limit"
                      value={format.number(
                        currentSubscription.maxServerlessRequestsPerMonth,
                        '0[.]0a',
                      )}
                    />
                  )}
                </>
              ) : (
                <>
                  {currentPlan.maxRequestsPerMonth && (
                    <Stat
                      label="Monthly max query volume"
                      value={format.number(
                        currentPlan.maxRequestsPerMonth,
                        '0[.]0a',
                      )}
                    />
                  )}
                  <Stat
                    label="Total members"
                    value={data?.account?.memberships?.length ?? 'n/a'}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </ChampionDashboardCardBody>
    </ChampionDashboardCard>
  );
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => {
  return (
    <div className="flex-1 text-center">
      <Text as="div" variant="code" size="2xl" className="text-primary">
        {value}
      </Text>
      <Heading as="h3" className="text-secondary">
        {label}
      </Heading>
    </div>
  );
};
